import { cn } from '@/lib';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from '@/design-system/icons';
import type { NotificationState } from '../types';

const ICON_BUTTON_CLASS =
  'size-6 flex items-center justify-center rounded opacity-80 group-hover:opacity-100 hover:bg-surface-darker transition-opacity cursor-pointer';

export interface NotificationContainerProps {
  icon: ReactNode;
  children: ReactNode;
  timestamp: string;
  state: NotificationState;
  onClick?: () => void;
  onDelete?: () => void;
  className?: string;
}

export function NotificationContainer({
  icon,
  children,
  timestamp,
  state,
  onClick,
  onDelete,
  className,
}: NotificationContainerProps) {
  const { t } = useTranslation();
  const isUnseen = state === 'unseen';

  // The action sits inside a clickable row, so it may not bubble up into it
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'group flex gap-3 p-3 rounded-lg transition-colors',
        onClick && 'hover:bg-surface-darker',
        isUnseen && 'bg-surface-darker/50',
        className
      )}
    >
      <div className="shrink-0 mt-0.5">{icon}</div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">{children}</div>
          <div className="shrink-0 flex items-center gap-1">
            {isUnseen && (
              <span className="size-2 mt-1.5 rounded-full bg-primary" />
            )}

            {onDelete && (
              <button
                type="button"
                onClick={handleDeleteClick}
                aria-label={t('notifications.delete')}
                className={ICON_BUTTON_CLASS}
              >
                <Trash2 className="size-3.5 text-muted" />
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted mt-1">{timestamp}</p>
      </div>
    </div>
  );
}
